import { Link, useNavigate } from 'react-router-dom';
import { Container } from '@/components/layout/container';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CartItem } from '@/features/cart/components/cart-item';
import { CartSummary } from '@/features/cart/components/cart-summary';
import { useCart } from '@/features/cart/hooks/use-cart';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { cn } from '@/lib/utils';
import { ShoppingBag, Phone, MessageCircle } from 'lucide-react';

export function CartPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items, itemCount, clearCart } = useCart(user?.id);

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-brand-50 py-16">
        <Container>
          <Card className="mx-auto max-w-xl text-center">
            <CardContent className="px-6 py-14 sm:px-10 sm:py-16">
              <ShoppingBag className="mx-auto mb-4 h-16 w-16 text-brand-300" />
              <h1 className="mb-2 text-2xl font-display font-bold text-brand-900">
                Your Cart is Empty
              </h1>
              <p className="mb-6 text-brand-600">
                Browse our catalog to find amazing products
              </p>
              <Link to="/catalog" className={cn(buttonVariants({ variant: 'primary', size: 'md' }))}>
                Continue Shopping
              </Link>
            </CardContent>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-50 py-4 sm:py-8">
      <Container>
        <div className="mb-6 flex flex-col gap-4 rounded-3xl bg-white p-5 shadow-sm sm:mb-8 sm:p-6">
          <div>
            <h1 className="text-2xl font-display font-bold text-brand-900 sm:text-3xl">
              Shopping Cart ({itemCount} items)
            </h1>
            <p className="mt-1 text-sm text-brand-600 sm:text-base">
              Review your selections, adjust quantities, then proceed to checkout.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Link
              to="/catalog"
              className={cn(buttonVariants({ variant: 'outline', size: 'md' }), 'w-full')}
            >
              Continue Shopping
            </Link>
            <Button variant="ghost" onClick={clearCart} className="w-full">
              Clear Cart
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-4 sm:p-6">
                {items.map((item) => (
                  <CartItem key={item.id} item={item} customerId={user?.id} />
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Order Summary + Quick checkout actions */}
          <div>
            <CartSummary onCheckout={() => navigate('/checkout')} />
            <div className="mt-3 flex flex-col gap-2">
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => navigate('/checkout?action=call')}
              >
                <Phone className="w-4 h-4" />
                Checkout then Call
              </Button>
              <Button
                variant="outline"
                className="w-full gap-2 bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                onClick={() => navigate('/checkout?action=whatsapp')}
              >
                <MessageCircle className="w-4 h-4" />
                Checkout then WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}

export default CartPage;
